import { join } from 'node:path';
import { readFile } from 'node:fs/promises';
import { GridAppLayout } from '@wix/ambassador-velo-apps-v1-app/types';
import { getSourceFolder } from '@wix/velo-github-layout-definitions';
import normalizePath from 'normalize-path';
import { pathExists } from '@wix/cli-fs';
import { CliError, CliErrorCode } from '@wix/cli-error';
import {
  WIX_LOCK_FILENAME,
  DEPENDENCIES_METADATA_FILENAME,
} from '@wix/cli-core-definitions';
import { readFiles } from '../utils.js';

interface File {
  path: string;
  content: string;
}

async function readFileOrThrow(path: string): Promise<string> {
  try {
    return await readFile(path, 'utf-8');
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.FailedToReadProjectFiles(),
      cause: e,
    });
  }
}

async function readFilesOrThrow(root: string): Promise<File[]> {
  try {
    return await readFiles(root);
  } catch (e) {
    throw new CliError({
      code: CliErrorCode.FailedToReadProjectFiles(),
      cause: e,
    });
  }
}

async function loadProjectRootFiles(projectFolder: string) {
  const [wixLockPath, dependenciesMetadataPath] = await Promise.all([
    pathExists(join(projectFolder, WIX_LOCK_FILENAME)),
    pathExists(join(projectFolder, DEPENDENCIES_METADATA_FILENAME)),
  ]);

  if (wixLockPath) {
    return Promise.all(
      [
        'package.json',
        WIX_LOCK_FILENAME,
        ...(dependenciesMetadataPath ? [DEPENDENCIES_METADATA_FILENAME] : []),
      ].map(async (path) => {
        const content = await readFileOrThrow(join(projectFolder, path));

        return { path, content };
      })
    );
  }

  return [];
}

export async function loadProjectGridAppContent(projectFolder: string) {
  const sourceFiles = (
    await readFilesOrThrow(getSourceFolder(projectFolder))
  ).map((file) => ({
    content: file.content,
    path: normalizePath(join(getSourceFolder(''), file.path)),
  }));

  const rootFiles = await loadProjectRootFiles(projectFolder);

  return {
    ignoreForbiddenPaths: true,
    layout: GridAppLayout.GITHUB,
    files: sourceFiles.concat(rootFiles),
  };
}
